/**
 * HTML parser for the diff engine.
 *
 * Parses an HTML string into a normalized domhandler Element tree.
 * All normalization happens here so the differ only sees clean,
 * unambiguous elements.
 */
import * as cheerio from 'cheerio';
import type { CheerioAPI } from 'cheerio';
import type { Element } from 'domhandler';
import render from 'dom-serializer';
import katex from 'katex';

import { normalizeDefaultCellSpans } from './normalizeImportArtifacts';
import { stripZeroWidthSpaces } from '../text';
import { TEMPLATE_FIELD_KINDS, templateFieldWrapperClass } from '../templateFields';

export const CHANGES_MATH_NODE_NAME = 'changes-math';

// Matches inline math delimiters \(...\). Non-greedy: stops at the first \).
// Plain parentheses like f(x) inside the formula are fine - they don't match \).
// Only fails on nested escaped delimiters like \( f\(x\) \) which is non-standard LaTeX.
export const INLINE_MATH_REGEX = /\\\(([\s\S]+?)\\\)/g;

// A4 portrait content width: (210mm - 2×18mm padding) at ~96dpi ≈ 655px
const MAX_TABLE_WIDTH_PORTRAIT = 655;

// ---------------------------------------------------------------------------
// Normalization steps
// ---------------------------------------------------------------------------

/**
 * Convert <title-node> custom tag into standard <p class="title-node">
 * so DOMPurify does not strip it. Remove empty title-nodes - TipTap
 * always serializes the node even when empty, but it's invisible in the UI.
 */
const normalizeTitleNode = ($: CheerioAPI, $root: cheerio.Cheerio<Element>): void => {
  $root.find('title-node').each((_, el) => {
    if (!$(el).text().trim()) {
      $(el).remove();
      return;
    }
    (el as Element).tagName = 'p';
    (el as Element).attribs.class = 'title-node';
  });
};

/**
 * Convert template-field custom tags into standard HTML with wrapper classes
 * so existing CSS in _content-template-fields.scss applies.
 */
const normalizeTemplateFields = ($root: cheerio.Cheerio<Element>): void => {
  for (const { name, block } of TEMPLATE_FIELD_KINDS) {
    $root.find(name).each((_, el) => {
      (el as Element).tagName = block ? 'div' : 'span';
      (el as Element).attribs.class = templateFieldWrapperClass(name);
    });
  }
};

/**
 * Remove rendered heading artifacts so the differ sees only semantic content.
 *
 * - .heading-label spans (numbering like "1.2") → removed
 * - .heading-text / .content / [data-node-view-content] wrappers → unwrapped
 * - ProseMirror internal elements → removed
 *
 * These artifacts appear in editor HTML (editor.getHTML()) and possibly in
 * older stored blobs.  Stored HTML from current TipTap is already clean,
 * but we normalize defensively.
 */
const normalizeRenderedHeadings = ($: CheerioAPI, $root: cheerio.Cheerio<Element>): void => {
  $root.find('h1, h2, h3, h4, h5, h6').each((_, heading) => {
    const $heading = $(heading);

    // Remove numbering labels
    $heading.children('.heading-label').remove();

    // Unwrap content wrappers (replace wrapper with its children)
    $heading
      .children('.heading-text, .content, [data-node-view-content], [data-node-view-content-react]')
      .each((_, wrapper) => {
        const $wrapper = $(wrapper);
        $wrapper.replaceWith($wrapper.html() || '');
      });

    // Remove ProseMirror internal elements
    $heading.find('.ProseMirror-separator, .ProseMirror-trailingBreak').remove();
  });
};

/**
 * Caption nodes may contain a ZWSP placeholder in editor HTML so Safari can place
 * the caret inside an otherwise empty caption. The diff/render engine should treat
 * that placeholder as non-content.
 */
const normalizeCaptionPlaceholders = ($: CheerioAPI, $root: cheerio.Cheerio<Element>): void => {
  $root.find('caption-node').each((_, caption) => {
    $(caption)
      .find('*')
      .addBack()
      .contents()
      .each((__, node) => {
        if (node.type !== 'text') return;

        const cleaned = stripZeroWidthSpaces(node.data || '');
        if (cleaned) {
          node.data = cleaned;
          return;
        }

        $(node).remove();
      });
  });
};

const convertColumnWidths = ($: CheerioAPI, $root: cheerio.Cheerio<Element>): void => {
  $root.find('table').each((_, table) => {
    const $table = $(table);
    const $colgroup = $table.children('colgroup').first();
    if (!$colgroup.length) return;

    const colWidthsPx: number[] = [];
    $colgroup.children('col').each((_, col) => {
      const style = $(col).attr('style') || '';
      const widthMatch = style.match(/(?<![a-z-])width\s*:\s*([\d.]+)(%|px|em|rem)?/i);

      if (!widthMatch) {
        colWidthsPx.push(0);
        return;
      }

      const value = parseFloat(widthMatch[1]);
      const unit = widthMatch[2] || '';

      if (unit === '%') {
        colWidthsPx.push(Math.floor((value / 100) * MAX_TABLE_WIDTH_PORTRAIT));
      } else if (unit === 'px' || unit === '') {
        colWidthsPx.push(Math.floor(value));
      } else {
        colWidthsPx.push(Math.floor(value * 16));
      }
    });

    if (colWidthsPx.length === 0 || colWidthsPx.every((width) => width === 0)) {
      $colgroup.remove();
      return;
    }

    // Own rows only - `find` would reach into a nested table and stamp this table's widths onto it.
    $table.find('> tbody > tr, > thead > tr, > tr').each((_, tr) => {
      let columnIndex = 0;

      $(tr)
        .children('td, th')
        .each((_, cell) => {
          const $cell = $(cell);
          const colspan = parseInt($cell.attr('colspan') || '1', 10);
          const widths: number[] = [];

          for (let offset = 0; offset < colspan && columnIndex + offset < colWidthsPx.length; offset += 1) {
            const width = colWidthsPx[columnIndex + offset];
            if (width > 0) widths.push(width);
          }

          if (widths.length > 0) {
            $cell.attr('colwidth', widths.join(','));
          }

          columnIndex += colspan;
        });
    });

    $colgroup.remove();
  });
};

/**
 * Convert inline math delimiters \(...\) into dedicated <changes-math> elements.
 *
 * This ensures the differ treats math formulas as atomic nodes rather than
 * diffing the raw LaTeX character by character.
 */
const materializeInlineMath = ($: CheerioAPI, $root: cheerio.Cheerio<Element>): void => {
  // Walk all text nodes and replace math patterns
  $root
    .find('*')
    .contents()
    .each((_, node) => {
      if (node.type !== 'text') return;

      const text = (node as unknown as { data: string }).data;
      if (!text || !text.includes('\\(')) return;

      // Skip math inside code/pre blocks or already-materialized math nodes
      const $node = $(node);
      if ($node.closest(`${CHANGES_MATH_NODE_NAME}, code, pre`).length > 0) return;

      INLINE_MATH_REGEX.lastIndex = 0;
      if (!INLINE_MATH_REGEX.test(text)) return;

      INLINE_MATH_REGEX.lastIndex = 0;
      const parts: string[] = [];
      let lastIndex = 0;
      let match: RegExpExecArray | null;

      while ((match = INLINE_MATH_REGEX.exec(text))) {
        const [fullMatch, formula] = match;
        if (match.index > lastIndex) {
          parts.push(escapeForCheerio(text.slice(lastIndex, match.index)));
        }
        parts.push(
          `<${CHANGES_MATH_NODE_NAME} data-formula="${escapeAttr(formula)}">${escapeForCheerio(fullMatch)}</${CHANGES_MATH_NODE_NAME}>`
        );
        lastIndex = match.index + fullMatch.length;
      }

      if (lastIndex < text.length) {
        parts.push(escapeForCheerio(text.slice(lastIndex)));
      }

      $node.replaceWith(parts.join(''));
    });
};

const escapeAttr = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const escapeForCheerio = (s: string): string => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * Unwrap comment annotation wrappers (`selection-mark`, `selection-node`).
 * These are editor-only annotations that should not influence the diff.
 */
const stripCommentSelections = ($: CheerioAPI): void => {
  $('selection-node, selection-mark').each((_i, el) => {
    $(el).replaceWith($(el).html() || '');
  });
};

/**
 * Strip trailing empty `<p>` without data-id.
 * TipTap adds one for cursor positioning; it is not real content.
 */
const removeTrailingEmptyParagraphs = ($: CheerioAPI, $root: cheerio.Cheerio<Element>): void => {
  const children = $root.children().toArray();
  for (let i = children.length - 1; i >= 0; i--) {
    const el = children[i] as Element;
    if (el.tagName !== 'p' || el.attribs?.['data-id'] || $(el).children().length > 0 || $(el).text().trim()) break;
    $(el).remove();
  }
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Parses an HTML string into a normalized Element tree ready for diffing. */
export const parseHtml = (html: string): Element => {
  const $ = cheerio.load(html);
  const $root = $('body') as cheerio.Cheerio<Element>;

  stripCommentSelections($);
  normalizeTitleNode($, $root);
  normalizeTemplateFields($root);
  normalizeRenderedHeadings($, $root);
  normalizeCaptionPlaceholders($, $root);
  normalizeDefaultCellSpans($, $root);
  convertColumnWidths($, $root);
  materializeInlineMath($, $root);
  $root.find('colgroup').remove();
  removeTrailingEmptyParagraphs($, $root);

  return $root.get(0)!;
};

/**
 * Serializes a domhandler Element back to an HTML string.
 * Used by the renderer to reconstruct element HTML.
 */
export const serializeElement = (el: Element): string => render(el);

/**
 * Renders a math formula to a KaTeX HTML string.
 * Used by the renderer for changes-math atom nodes.
 */
export const renderMathFormula = (formula: string): string => {
  try {
    return katex.renderToString(formula, { throwOnError: true });
  } catch {
    // Invalid katex falls back to the raw formula body, escaped so `<`/`&` never become markup - same as the editor.
    return escapeForCheerio(formula);
  }
};

export const renderMathFormulaCached = (formula: string, cache?: Map<string, string>): string => {
  if (!cache) return renderMathFormula(formula);
  const cached = cache.get(formula);
  if (cached !== undefined) return cached;
  const result = renderMathFormula(formula);
  cache.set(formula, result);
  return result;
};
